import CallOutlinedIcon from "@mui/icons-material/CallOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import { useCallback, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { canUsePowerDialer } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import ConfirmStartPowerDialerDialog from "./ConfirmStartPowerDialerDialog"
import { usePowerDialer } from "./PowerDialerProvider"

export function usePowerDialerConfirm({
  source,
  filters,
  ordering,
  selectedIds,
  disabled = false,
  disabledReason,
} = {}) {
  const { startSession, isStarting, hasOpenSession } = usePowerDialer()
  const [pendingSession, setPendingSession] = useState(null)
  const isDisabled = Boolean(disabled || hasOpenSession)
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds

  const handleStartClicked = useCallback(() => {
    if (isDisabled) return
    setPendingSession({
      source,
      filters: filters || [],
      ordering: ordering || "",
      selectedIds: (selectedIdsRef.current || []).map((id) => String(id)),
    })
  }, [filters, isDisabled, ordering, source])

  const handleConfirmClosed = useCallback(() => {
    if (isStarting) return
    setPendingSession(null)
  }, [isStarting])

  const handleConfirmStart = useCallback(
    (sessionName, amd) => {
      if (!pendingSession) return
      startSession({ ...pendingSession, name: sessionName, amd }).then(
        (started) => {
          if (started) setPendingSession(null)
        }
      )
    },
    [pendingSession, startSession]
  )

  const confirmDialog = (
    <ConfirmStartPowerDialerDialog
      open={Boolean(pendingSession)}
      source={pendingSession?.source}
      filters={pendingSession?.filters}
      ordering={pendingSession?.ordering}
      selectedIds={pendingSession?.selectedIds}
      isStarting={isStarting}
      onClose={handleConfirmClosed}
      onConfirm={handleConfirmStart}
    />
  )

  return {
    handleStartClicked,
    confirmDialog,
    isStarting,
    isDisabled,
    disabledReason,
    hasOpenSession,
  }
}

export default function StartPowerDialerButton({
  source,
  filters,
  ordering,
  selectedIds,
  disabled = false,
  disabledReason,
}) {
  const profile = useSelector(selectProfile)
  const {
    handleStartClicked,
    confirmDialog,
    isStarting,
    isDisabled,
    disabledReason: reason,
  } = usePowerDialerConfirm({
    source,
    filters,
    ordering,
    selectedIds,
    disabled,
    disabledReason,
  })

  if (!canUsePowerDialer(profile)) return null

  return (
    <>
      <LoadingButton
        variant="outlined"
        color="primary"
        startIcon={<CallOutlinedIcon />}
        loading={isStarting}
        disabled={isDisabled}
        title={isDisabled && reason ? reason : undefined}
        onClick={handleStartClicked}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Power Dialer
      </LoadingButton>
      {confirmDialog}
    </>
  )
}
